import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Dispensary } from "@/custom-types/Dispensary";
import { Strain } from "@/custom-types/Strain";
import { Preferences } from "@/custom-types/StrainPreferences";
import {
  CardImageDataResponse,
  FulfillmentPreference,
  MedCard,
  parseFulfillmentPreference,
  User,
  UserReviewsApiResponse,
  UserState,
} from "@/custom-types/User";
import {
  LOAD_COOKIES,
  LoadCookiesAction,
} from "@/redux/action-creators/cookies";
import { HYDRATE } from "@/redux/reducers/hydration";
import { MENU_TITLES, menuTypeCookieName } from "@/utils/menuTypeUtils";

type CardType = "government" | "medical";

export const initialState: UserState = {
  fulfillmentPreference: undefined,
  governmentCardImageData: undefined,
  isLoggedOutRedirect: false,
  loading: true,
  medicalCardImageData: undefined,
  prefersMedical: undefined,
};

export const parseUserId = (
  cookieValue: string | undefined | null,
): number | undefined => {
  if (cookieValue) {
    const id = parseInt(cookieValue);
    if (!isNaN(id)) {
      return id;
    }
  }
  return undefined;
};

const userSlice = createSlice({
  extraReducers: (builder) => {
    /**
     * Ignore any hydration events after initial page load to preserve any
     * subsequent changes that have been made on the client.
     */
    builder.addCase(HYDRATE, () => {});

    builder.addCase(LOAD_COOKIES, (state, action: LoadCookiesAction) => {
      const preference = action?.cookies["userFulfillmentPreference"];

      return {
        ...state,
        fulfillmentPreference:
          parseFulfillmentPreference(preference) ||
          FulfillmentPreference.DELIVERY,
        id:
          state.id ||
          parseUserId(action.cookies && action.cookies["leafly.userid"]),
        loading: false,
        prefersMedical:
          typeof state.prefersMedical === "boolean"
            ? state.prefersMedical
            : action.cookies &&
              (action.cookies[menuTypeCookieName] || "").toLowerCase() ===
                MENU_TITLES.med.shortLower,
      };
    });
  },
  initialState,
  name: "user",
  reducers: {
    changeFulfillmentPreference: (
      state,
      action: PayloadAction<FulfillmentPreference>,
    ) => {
      state.fulfillmentPreference =
        parseFulfillmentPreference(action.payload) ||
        FulfillmentPreference.DELIVERY;
    },
    changeMenuPreference: (state, action: PayloadAction<string>) => {
      state.prefersMedical = action.payload.toLowerCase() === "med";
    },
    loadFavorites: (state) => {
      state.favoritedStrains = {
        ...state.favoritedStrains,
        loading: true,
      };
      state.followedDispensaries = {
        ...state.followedDispensaries,
        loading: true,
      };
    },
    loadFavoritesDispensaries: (state) => {
      state.followedDispensaries = {
        ...state.followedDispensaries,
        loading: true,
      };
    },
    loadFavoritesMoreResults: (
      state,
      action: PayloadAction<{ strains: Strain[]; totalCount: number }>,
    ) => {
      state.favoritedStrains = {
        loading: false,
        strains: [
          ...(state?.favoritedStrains?.strains || []),
          ...action.payload.strains,
        ],
        totalCount: state?.favoritedStrains?.totalCount,
      };
    },
    loadFavoritesResults: (
      state,
      action: PayloadAction<{ strains: Strain[]; totalCount: number }>,
    ) => {
      state.favoritedStrains = {
        loading: false,
        strains: action.payload.strains,
        totalCount: action.payload.totalCount,
      };
    },
    loadFollowedDispensariesResults: (
      state,
      action: PayloadAction<{ dispensaries: Dispensary[]; totalCount: number }>,
    ) => {
      state.followedDispensaries = {
        dispensaries: [
          ...(state?.followedDispensaries?.dispensaries || []),
          ...action.payload.dispensaries,
        ],
        loading: false,
        totalCount: action.payload.totalCount,
      };
    },
    loadIdCardsFailure: (state) => {
      state.governmentCardImageData = {
        ...state.governmentCardImageData,
        loading: false,
      };
      state.medicalCardImageData = {
        ...state.medicalCardImageData,
        loading: false,
      };
    },
    loadIdCardsRequest: (state) => {
      state.governmentCardImageData = {
        ...state.governmentCardImageData,
        loading: true,
      };
      state.medicalCardImageData = {
        ...state.medicalCardImageData,
        loading: true,
      };
    },
    loadIdCardsSuccess: (
      state,
      action: PayloadAction<CardImageDataResponse>,
    ) => {
      state.governmentCardImageData = {
        data: {
          createdAt: action.payload.stateIdUpdatedAt,
          key: action.payload.stateIdS3Key,
        },
        loading: false,
      };
      state.medicalCardImageData = {
        data: {
          createdAt: action.payload.medicalIdUpdatedAt,
          key: action.payload.medicalIdS3Key,
        },
        loading: false,
      };
    },
    loadMedCard: (state, action: PayloadAction<MedCard>) => {
      state.medCard = action.payload;
    },
    loadReviews: (state) => {
      state.reviews = {
        ...state.reviews,
        loading: true,
      };
    },
    loadReviewsResults: (
      state,
      action: PayloadAction<UserReviewsApiResponse>,
    ) => {
      state.reviews = {
        items: [...(state?.reviews?.items || []), ...action.payload.data],
        loading: false,
        totalCount: action.payload?.metadata?.totalCount || 0,
      };
    },
    loadStrainPreferences: (state) => {
      state.strainPreferences = {
        ...state.strainPreferences,
        loading: true,
      };
    },
    loadStrainPreferencesResults: (
      state,
      action: PayloadAction<{
        preferences: Preferences[];
        strainSlug?: string;
        strainMatchQuery?: string;
      }>,
    ) => {
      state.strainPreferences = {
        loading: false,
        preferences: action.payload.preferences,
        strainMatchQuery: action.payload.strainMatchQuery,
        strainSlug: action.payload.strainSlug,
      };
    },
    loadUser: (state, action: PayloadAction<User>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    saveIdFail: (
      state,
      action: PayloadAction<{
        cardType: CardType;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
        error: any;
      }>,
    ) => {
      const { cardType, error } = action.payload;
      state[`${cardType}CardImageData`] = {
        ...state[`${cardType}CardImageData`],
        error,
        loading: false,
      };
    },
    saveIdRequest: (state, action: PayloadAction<CardType>) => {
      const cardType = action.payload;
      state[`${cardType}CardImageData`] = {
        ...state[`${cardType}CardImageData`],
        loading: true,
      };
    },
    saveIdSuccess: (
      state,
      action: PayloadAction<{
        cardType: CardType;
        createdAt: string | null;
        key: string | null;
      }>,
    ) => {
      const { cardType, ...payload } = action.payload;
      state[`${cardType}CardImageData`] = {
        data: payload,
        error: null,
        loading: false,
      };
    },
    setLoggedOutRedirect: (state, action: PayloadAction<boolean>) => {
      state.isLoggedOutRedirect = action.payload;
    },
  },
});

export const {
  changeFulfillmentPreference,
  changeMenuPreference,
  loadFavorites,
  loadFavoritesDispensaries,
  loadFavoritesMoreResults,
  loadFavoritesResults,
  loadFollowedDispensariesResults,
  loadIdCardsFailure,
  loadIdCardsRequest,
  loadIdCardsSuccess,
  loadMedCard,
  loadReviews,
  loadReviewsResults,
  loadStrainPreferences,
  loadStrainPreferencesResults,
  loadUser,
  saveIdFail,
  saveIdRequest,
  saveIdSuccess,
  setLoggedOutRedirect,
} = userSlice.actions;

export default userSlice.reducer;
